import React, { useEffect, useMemo } from 'react'
import * as yup from "yup";
import { Modal } from "../layout";
import { useValidatedState, ValidationHint } from '../validation/ValidatedState'
import { Label } from '../labels/Label'
import { TextInput } from '../inputs/TextInput'
import { Select, SelectOption } from "../controls/Select";
import { autoFocus } from '../labels/Focus';

export interface CountryStates {
    [country: string]: string[]
}

export interface Address {
    addressLine1?: string
    suburb?: string
    state?: string
    postCode?: string
    country?: string
}

export const AddressSchema: yup.ObjectSchema<Address> = yup.object({
    addressLine1: yup
        .string()
        .required("Street number and name is required")
        .defined("Street number and name is required"),
    suburb: yup
        .string()
        .required("Suburb is required")
        .defined("Suburb is required"),
    state: yup
        .string()
        .required("State is required")
        .defined("State is required"),
    postCode: yup
        .string()
        .required("Post code is required")
        .defined("Post code is required"),
    country: yup
        .string()
        .required("Country is required")
        .defined("Country is required")
});

export interface AddressModalProps {
    id: string
    open: boolean
    onClose: () => any
    value: Address
    onChange: (v: Address) => any
    countryStates?: CountryStates
}

export const AddressModal: React.FC<AddressModalProps> = (props) => {
    const [value, setValue, errors, validate] = useValidatedState<Address>({ ...props.value }, AddressSchema);
    useEffect(() => {
        if (value != props.value) {
            setValue({ ...props.value }, ValidationHint.ClearValidation);
        }
    }, [props.value])

    const onChange = () => {
        validate(`#${props.id}`).then(res => {
            if (res) {
                props.onChange(value);
                props.onClose();
            }
        })
    }

    const countryOptions = useMemo(() => {
        let res: SelectOption<string>[] = []
        if (!props.countryStates) {
            return res;
        }
        for (let c in props.countryStates) {
            res.push({ label: c, value: c })
        }
        res.sort((a, b) => {
            if (a > b) {
                return 1;
            }
            else if (a < b) {
                return -1;
            }
            else {
                return 0;
            }
        });
        return res;
    }, [props.countryStates]);

    const stateOptions = useMemo(() => {
        let res: SelectOption<string>[] = []
        if (!props.countryStates) {
            return res;
        }
        if (value.country && value.country in props.countryStates) {
            res = props.countryStates[value.country].map(r => ({ label: r, value: r }));
        }
        res.sort((a, b) => {
            if (a > b) {
                return 1;
            }
            else if (a < b) {
                return -1;
            }
            else {
                return 0;
            }
        });
        return res;
    }, [value.country, props.countryStates]);

    useEffect(() => {
        if (props.countryStates) {
            setValue({ state: '' }, ValidationHint.NoValidation);
        }
    }, [value.country])

    return <Modal title="Enter address" id={props.id} open={props.open} onClose={props.onClose} onOpened={() => autoFocus(`#${props.id}`)} rightButtons={[
        { label: "OK", onClick: onChange },
        { label: "Close", onClick: props.onClose, mode: 'secondary' }
    ]}>
        <div className="row">
            <div className="col">
                <Label
                    label="Street number and name"
                    id={`${props.id}-addressLine1`}
                    error={errors.addressLine1}
                    required
                >
                    <TextInput
                        size="block"
                        value={value.addressLine1 || ''}
                        onChange={v => setValue({ addressLine1: v })}
                    />
                </Label>
            </div>
        </div>
        <div className="row">
            <div className="col-md-6">
                <Label
                    className="mt-2"
                    label="Suburb"
                    id={`${props.id}-suburb`}
                    required
                    error={errors.suburb}
                >
                    <TextInput
                        size="block"
                        value={value.suburb || ''}
                        onChange={v => setValue({ suburb: v })}
                    />
                </Label>
            </div>
            <div className="col-md-6">
                <Label
                    className="mt-2"
                    label="Post code"
                    id={`${props.id}-postCode`}
                    error={errors.postCode}
                    required
                >
                    <TextInput
                        size="block"
                        value={value.postCode || ''}
                        onChange={v => setValue({ postCode: v })}
                    />
                </Label>
            </div>
        </div>
        <div className="row">
            <div className="col-md-6">
                <Label
                    className="mt-2"
                    label="Country"
                    id={`${props.id}-country`}
                    error={errors.country}
                    required
                >
                    {props.countryStates ? <Select<string>
                        value={value.country ? value.country : null}
                        onChange={v => setValue({ country: v ? v : '' })}
                        options={countryOptions}
                        defaultLabel="Select..."
                    /> : <TextInput
                        size="block"
                        value={value.country || ''}
                        onChange={v => setValue({ country: v })}
                    />}
                </Label>
            </div>
            <div className="col-md-6">
                <Label
                    className="mt-2"
                    label="State"
                    id={`${props.id}-state`}
                    error={errors.state}
                    required
                >
                    {props.countryStates ? <Select<string>
                        value={value.state ? value.state : null}
                        onChange={v => setValue({ state: v ? v : '' })}
                        options={stateOptions}
                        defaultLabel="Select..."
                    /> : <TextInput
                        size="block"
                        value={value.state || ''}
                        onChange={v => setValue({ state: v })}
                    />}
                </Label>
            </div>
        </div>
    </Modal>
}